/**
 * Defers inline CMS <script> execution without removing the tags.
 *
 * Server: rewrite executable scripts' `type` attribute to a non-executable
 * token so the browser parser skips them. Original type goes into
 * `data-original-type`.
 *
 * Client (post-hydration): replace each placeholder in place with a real
 * <script>. Running in place preserves `document.currentScript.parentNode`,
 * which widgets rely on to mount where the author put them.
 *
 * SSR and CSR markup stay byte-identical so hydration sees no script-shaped
 * mismatch.
 */

import { log } from "./commons";

const DEFERRED_TYPE = "application/x-deferred-cms-script";

const SCRIPT_TAG_RE = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
const TYPE_ATTR_RE = /\btype\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const JS_TYPE_RE = /^(text|application)\/(java|ecma)script$/;

const SKIP_ATTRS = new Set([
  "type",
  "data-original-type",
  "data-cms-activated"
]);

function isExecutableScriptType(type) {
  if (!type) return true;
  const t = type.trim().toLowerCase().split(";")[0];
  return !t || t === "module" || JS_TYPE_RE.test(t);
}

function extractType(attrs) {
  const m = TYPE_ATTR_RE.exec(attrs);
  return m ? m[1] ?? m[2] ?? m[3] ?? "" : "";
}

/**
 * Rewrite executable <script> tags to a deferred placeholder type so the
 * browser will not run them on parse. Non-executable scripts (ld+json,
 * application/json, etc.) pass through untouched.
 * @param {string} html
 * @returns {string}
 */
export function disableInlineScripts(html) {
  if (!html || typeof html !== "string" || !/<script\b/i.test(html)) {
    return html;
  }
  return html.replace(SCRIPT_TAG_RE, (match, attrs, body) => {
    const originalType = extractType(attrs);
    if (!isExecutableScriptType(originalType)) return match;
    const rest = attrs.replace(TYPE_ATTR_RE, "").replace(/\s+/g, " ").trim();
    const sep = rest ? ` ${rest}` : "";
    const escaped = originalType.replace(/"/g, "&quot;");
    return `<script type="${DEFERRED_TYPE}" data-original-type="${escaped}"${sep}>${body}</script>`;
  });
}

/**
 * Log deferred CMS HTML on server-side only.
 * @param {string} html
 * @param {string} [logTrace]
 * @param {string} [blockName]
 */
export function logDeferredCmsHtmlServer(html, logTrace = "", blockName = "") {
  if (
    typeof window !== "undefined" ||
    !html ||
    typeof html !== "string" ||
    !/<script\b/i.test(html)
  ) {
    return;
  }
  const suffix = blockName ? `;DEFERRED_CMS_BLOCK_NAME=${blockName}` : "";
  log.info(`${logTrace};EVENT_NAME=EVENT_DEFERRED_CMS_HTML_SSR${suffix}`);
}

function fireImmediately(fn, eventName) {
  try {
    fn(new Event(eventName));
  } catch (e) {
    console.warn(`[cms-deferred-scripts] ${eventName} callback error:`, e);
  }
}

let shimDepth = 0;
let origDocAdd = null;
let origWinAdd = null;

function shimmedDocAdd(type, fn, opts) {
  if (
    type === "DOMContentLoaded" &&
    document.readyState !== "loading" &&
    typeof fn === "function"
  ) {
    fireImmediately(fn, "DOMContentLoaded");
    return undefined;
  }
  return origDocAdd.call(document, type, fn, opts);
}

function shimmedWinAdd(type, fn, opts) {
  if (typeof fn === "function") {
    // DOMContentLoaded bubbles to window; some CMS scripts register it there.
    if (type === "DOMContentLoaded" && document.readyState !== "loading") {
      fireImmediately(fn, "DOMContentLoaded");
      return undefined;
    }
    if (type === "load" && document.readyState === "complete") {
      fireImmediately(fn, "load");
      return undefined;
    }
  }
  return origWinAdd.call(window, type, fn, opts);
}

/**
 * Patch addEventListener so DOMContentLoaded / load listeners registered
 * after those events have already fired still run. Activation happens after
 * hydration, well past initial document parse.
 *
 * Refcounted: concurrent activations from multiple DeferredCmsHtml instances
 * share one install. Originals are captured once (the real native methods,
 * not bound wrappers) and restored only when the last activation completes.
 *
 * @returns {() => void} restore function
 */
function installLateListenerShim() {
  shimDepth += 1;
  if (shimDepth === 1) {
    origDocAdd = document.addEventListener;
    origWinAdd = window.addEventListener;
    document.addEventListener = shimmedDocAdd;
    window.addEventListener = shimmedWinAdd;
  }
  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    shimDepth -= 1;
    if (shimDepth === 0) {
      document.addEventListener = origDocAdd;
      window.addEventListener = origWinAdd;
      origDocAdd = null;
      origWinAdd = null;
    }
  };
}

function activateOne(original) {
  original.setAttribute("data-cms-activated", "1");
  const parent = original.parentNode;
  if (!parent) return Promise.resolve();

  const s = document.createElement("script");
  for (const a of original.attributes) {
    if (!SKIP_ATTRS.has(a.name)) s.setAttribute(a.name, a.value);
  }
  const originalType = original.getAttribute("data-original-type") || "";
  if (originalType) s.setAttribute("type", originalType);

  if (original.hasAttribute("src")) {
    // Programmatically inserted external scripts default to async. Force
    // ordered execution so inline scripts that follow can rely on globals
    // defined here.
    s.async = false;
    return new Promise((resolve) => {
      s.addEventListener("load", () => resolve(), { once: true });
      s.addEventListener("error", () => resolve(), { once: true });
      parent.replaceChild(s, original);
    });
  }

  const body = original.textContent;
  if (!body || !body.trim()) return Promise.resolve();
  s.textContent = body;
  parent.replaceChild(s, original);
  return Promise.resolve();
}

/**
 * Replace deferred placeholder scripts inside `container` with executable
 * <script> elements at the same DOM position. Activates serially so an
 * inline script can depend on globals defined by an external script that
 * appeared before it in the source HTML.
 * @param {Element | null} container
 * @returns {Promise<void> | undefined}
 */
export function activateDeferredScripts(container) {
  if (typeof document === "undefined" || !container) return undefined;

  const placeholders = container.querySelectorAll(
    `script[type="${DEFERRED_TYPE}"]:not([data-cms-activated])`
  );
  if (placeholders.length === 0) return undefined;

  const restore = installLateListenerShim();
  return Array.from(placeholders)
    .reduce(
      (chain, original) =>
        chain.then(() =>
          activateOne(original).catch((e) => {
            console.warn(
              "[cms-deferred-scripts] activation step error:",
              e
            );
          })
        ),
      Promise.resolve()
    )
    .finally(restore);
}
